import { isBoolean, isNumber } from 'lodash-es'

import { System } from './System'
import { storage } from '../storage'
import { isTouch } from '../../client/utils'
import { ACCESSIBILITY_PREFS_VERSION, accessibilityDefaults, normalizeAccessibilityPrefs } from '../../client/accessibility-prefs.js'

/**
 * Client Prefs System
 *
 */
export class ClientPrefs extends System {
  constructor(world) {
    super(world)

    const isQuest = /OculusBrowser/.test(navigator.userAgent)

    const data = storage.get('prefs', {})

    // v2: reset ui scale for new mobile default (0.9)
    if (!data.v) {
      data.v = 2
      data.ui = null
    }
    // v3: reset shadows for new mobile default (med)
    if (data.v < 3) {
      data.v = 3
      data.shadows = null
    }
    // v4: reset shadows for new defaults (low or med)
    if (data.v < 4) {
      data.v = 4
      data.shadows = null
    }

    // v5: phones default to the lightweight rendering path. Players can still
    // re-enable individual effects from the graphics menu.
    if (data.v < 5) {
      data.v = 5
      if (isTouch) {
        data.postprocessing = null
        data.bloom = null
        data.ao = null
      }
    }

    const prefersReducedMotion = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches === true
    if (data.v < ACCESSIBILITY_PREFS_VERSION) {
      data.v = ACCESSIBILITY_PREFS_VERSION
      if (typeof data.highContrast !== 'boolean') data.highContrast = false
      if (typeof data.reduceMotion !== 'boolean') data.reduceMotion = prefersReducedMotion
      if (!isNumber(data.master)) data.master = 1
    }

    const accessibility = normalizeAccessibilityPrefs(data, { touch: isTouch, prefersReducedMotion })

    this.ui = accessibility.ui
    this.actions = accessibility.actions
    this.highContrast = accessibility.highContrast
    this.reduceMotion = accessibility.reduceMotion
    this.stats = isBoolean(data.stats) ? data.stats : false
    this.dpr = isNumber(data.dpr) ? data.dpr : 1
    this.shadows = data.shadows ? data.shadows : isTouch ? 'low' : 'med' // none, low=1, med=2048cascade, high=4096cascade
    this.postprocessing = isBoolean(data.postprocessing) ? data.postprocessing : !isTouch
    this.bloom = isBoolean(data.bloom) ? data.bloom : !isTouch
    this.ao = isBoolean(data.ao) ? data.ao : !isTouch
    this.music = isNumber(data.music) ? data.music : 1
    this.sfx = isNumber(data.sfx) ? data.sfx : 1
    this.voice = isNumber(data.voice) ? data.voice : 1
    this.master = accessibility.master
    this.v = data.v

    this.changes = null
  }

  init() {
    this.world.chat.bindCommand('stats', () => {
      this.setStats(!this.stats)
    })
  }

  preFixedUpdate() {
    if (!this.changes) return
    this.emit('change', this.changes)
    this.changes = null
  }

  modify(key, value) {
    if (this[key] === value) return
    const prev = this[key]
    this[key] = value
    if (!this.changes) this.changes = {}
    if (!this.changes[key]) this.changes[key] = { prev, value: null }
    this.changes[key].value = value
    this.persist()
  }

  async persist() {
    // a small delay to ensure prefs that crash dont persist (eg old iOS with UHD shadows etc)
    await new Promise(resolve => setTimeout(resolve, 2000))
    storage.set('prefs', {
      ui: this.ui,
      actions: this.actions,
      stats: this.stats,
      dpr: this.dpr,
      shadows: this.shadows,
      postprocessing: this.postprocessing,
      bloom: this.bloom,
      ao: this.ao,
      music: this.music,
      sfx: this.sfx,
      voice: this.voice,
      master: this.master,
      highContrast: this.highContrast,
      reduceMotion: this.reduceMotion,
      v: this.v,
    })
  }

  setUI(value) {
    this.modify('ui', value)
  }

  setActions(value) {
    this.modify('actions', value)
  }

  setStats(value) {
    this.modify('stats', value)
  }

  setDPR(value) {
    this.modify('dpr', value)
  }

  setShadows(value) {
    this.modify('shadows', value)
  }

  setPostprocessing(value) {
    this.modify('postprocessing', value)
  }

  setBloom(value) {
    this.modify('bloom', value)
  }

  setAO(value) {
    this.modify('ao', value)
  }

  setMusic(value) {
    this.modify('music', value)
  }

  setSFX(value) {
    this.modify('sfx', value)
  }

  setVoice(value) {
    this.modify('voice', value)
  }

  setMaster(value) {
    this.modify('master', value)
  }

  setHighContrast(value) {
    this.modify('highContrast', value === true)
  }

  setReduceMotion(value) {
    this.modify('reduceMotion', value === true)
  }

  resetAccessibility() {
    const defaults = accessibilityDefaults({
      touch: isTouch,
      prefersReducedMotion: globalThis.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches === true,
    })
    this.setUI(defaults.ui)
    this.setActions(defaults.actions)
    this.setHighContrast(defaults.highContrast)
    this.setReduceMotion(defaults.reduceMotion)
    this.setMaster(defaults.master)
  }

  destroy() {
    // ...
  }
}
